import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  AppBar,
  Toolbar,
  Button,
  Link as MuiLink
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

/**
 * TermsOfService Component
 * Displays the Terms & Conditions for Wyspur AI
 */
const TermsOfService = () => {
  const navigate = useNavigate();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Wyspur brand colors matching LandingPage
  const wyspurColors = {
    darkNavy: '#0D0D21',
    navy: '#161C3E',
    blue: '#0095FF',
    text: '#FFFFFF',
    border: '#3C3C5A'
  };

  return (
    <Box sx={{ bgcolor: wyspurColors.darkNavy, minHeight: '100vh' }}>
      {/* Navigation Header */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: wyspurColors.darkNavy,
          borderBottom: `1px solid ${wyspurColors.border}`
        }}
      >
        <Toolbar
          sx={{
            justifyContent: 'space-between',
            py: 1,
            px: { xs: 2, sm: 4, md: 6, lg: 8 }
          }}
        >
          {/* Logo */}
          <Box
            component="img"
            src="/logo-full-cropped.png"
            alt="Wyspur AI"
            sx={{
              height: { xs: '40px', md: '50px' },
              cursor: 'pointer',
              '&:hover': { opacity: 0.8 }
            }}
            onClick={() => navigate('/')}
          />

          {/* Navigation Links */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, md: 3 } }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/')}
              sx={{
                color: wyspurColors.text,
                borderColor: wyspurColors.blue,
                px: { xs: 2, md: 3 },
                py: { xs: 0.75, md: 1 },
                fontSize: { xs: '0.875rem', md: '1rem' },
                borderRadius: 2,
                textTransform: 'none',
                '&:hover': {
                  bgcolor: 'rgba(0, 149, 255, 0.1)',
                  borderColor: wyspurColors.blue
                }
              }}
            >
              Back to Home
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ py: { xs: 4, md: 8 }, px: { xs: 2, sm: 3 } }}>
        <Box sx={{ color: wyspurColors.text }}>
          {/* Title */}
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 700,
              mb: 2,
              textAlign: 'center',
              fontSize: { xs: '2rem', md: '2.5rem' }
            }}
          >
            TERMS & CONDITIONS
          </Typography>

          <Typography
            variant="subtitle1"
            sx={{
              textAlign: 'center',
              mb: 6,
              opacity: 0.8
            }}
          >
            Effective Date: November 2025
          </Typography>

          {/* Introduction */}
          <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8 }}>
            These Terms and Conditions ("Agreement") constitute a binding contract between SM Software & Consulting LLC, doing business as Wyspur AI ("Company," "we," or "us") and the user ("you") regarding access to and use of the Wyspur AI platform and related Services ("Services").
          </Typography>

          {/* Section 1 */}
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, mt: 4 }}>
            1. Account Registration
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8 }}>
            Users must create an account to access the Services. You agree to provide accurate and complete information and are responsible for maintaining the confidentiality of your login credentials.
          </Typography>

          {/* Section 2 */}
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, mt: 4 }}>
            2. Subscription and Payment
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8 }}>
            The Services are offered on a recurring subscription basis (monthly or annual). Payments are processed securely through Stripe.
            Refunds are not provided except in cases of documented system failure attributable to the Company. Upon cancellation, access remains active until the end of the current billing cycle.
          </Typography>

          {/* Section 3 */}
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, mt: 4 }}>
            3. Acceptable Use
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            You agree not to:
          </Typography>
          <Box component="ul" sx={{ mb: 4, pl: 4, lineHeight: 1.8 }}>
            <li>Copy, modify, resell, or distribute any component of the Services;</li>
            <li>Reverse-engineer, decompile, or attempt to extract source code;</li>
            <li>Use the Services in violation of applicable laws or to infringe upon others' rights.</li>
          </Box>

          {/* Section 4 */}
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, mt: 4 }}>
            4. Artificial Intelligence Disclaimer
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8 }}>
            The Services utilize automated and AI-based systems that may generate errors or inaccuracies. The Company makes no guarantees regarding the accuracy or reliability of automated outputs and disclaims any liability for decisions made based on such outputs.
          </Typography>

          {/* Section 5 */}
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, mt: 4 }}>
            5. Intellectual Property
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8 }}>
            All intellectual property rights in the Services, including but not limited to code, design, and trademarks, are owned by the Company. You retain ownership of your uploaded content but grant us a limited license to store and process it for operational purposes.
          </Typography>

          {/* Section 6 */}
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, mt: 4 }}>
            6. Limitation of Liability
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8 }}>
            To the fullest extent permitted by law, the Company shall not be liable for indirect, incidental, or consequential damages, including loss of profits or data, arising out of or relating to the use or inability to use the Services.
          </Typography>

          {/* Section 7 */}
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, mt: 4 }}>
            7. Termination
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8 }}>
            The Company reserves the right to suspend or terminate access without notice for any violation of this Agreement. Upon termination, your right to use the Services immediately ceases.
          </Typography>

          {/* Section 8 */}
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, mt: 4 }}>
            8. Governing Law
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8 }}>
            This Agreement shall be governed by and construed under the laws of the State of Delaware, USA, without regard to conflict of law principles.
          </Typography>

          {/* Section 9 */}
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, mt: 4 }}>
            9. Contact
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8 }}>
            For inquiries, please contact{' '}
            <MuiLink
              href="mailto:sean@wyspurai.com"
              sx={{
                color: wyspurColors.blue,
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              sean@wyspurai.com
            </MuiLink>
            .
          </Typography>
        </Box>
      </Container>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          bgcolor: wyspurColors.navy,
          py: { xs: 3, md: 4 },
          borderTop: `1px solid ${wyspurColors.border}`,
          mt: 8
        }}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <Typography
            variant="body2"
            sx={{ color: wyspurColors.text, opacity: 0.6, textAlign: 'center' }}
          >
            © {new Date().getFullYear()} Wyspur AI. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default TermsOfService;
